/**
 * The evidence under the raster: bubbles, pins, lines and stops.
 *
 * A raster says what a neighbourhood is like. These say what it is made of, and
 * each category brings its own kind:
 *
 * * **Crime** is 63,293 anonymised snap points, which is far too many to read
 *   as dots across a city. Zoomed out they are binned into bubbles carrying a
 *   count, and as the map goes in the bubbles split until each is the published
 *   point it came from. The binning is MapLibre's own clustering rather than a
 *   hand-rolled grid: it already bins in screen space at a radius, sums a
 *   property while it does it, and stops at a zoom.
 * * **Schools** are pins, lettered P or S and coloured by Ofsted's grade, and a
 *   tap prints the grade in Ofsted's own words rather than the 0-1 number this
 *   project invented to rank them with.
 * * **Transport** is the tube and rail lines themselves, with stations over
 *   them and bus stops once the map is well into a street.
 *
 * Only the selected category's evidence is on screen, and none of it is fetched
 * until that category is selected -- so a reader who never opens crime never
 * pays for its points.
 */

import { Popup } from "maplibre-gl";
import type { GeoJSONSource, Map as MapLibreMap, MapGeoJSONFeature } from "maplibre-gl";

import type { CrimeCategories, Data } from "./data.ts";
import { count, escapeHtml } from "./format.ts";
import type { Manifest } from "./manifest.ts";

/** The screen-space bin, in pixels. */
const CLUSTER_RADIUS = 64;
/** Past this zoom a bubble has split into the points it stands for. */
const CLUSTER_MAX_ZOOM = 14;
/** Where a crime dot starts carrying its own count. */
const DOT_COUNT_ZOOM = 16;
/** Where bus stops are worth drawing. */
const BUS_ZOOM = 15;

/** An Ofsted grade, red through amber to green; grey where none is published. */
const GRADE_COLOURS = ["#c0392b", "#e67e22", "#e8b923", "#7cb342", "#2e7d32"];
const UNGRADED = "#9a958c";

/** One colour per class of track. */
const RAILWAY_COLOURS: Record<string, string> = {
  subway: "#2a4b9b",
  light_rail: "#00a19a",
  tram: "#4a9b4a",
  rail: "#6b6f76",
};

/** The layers a click opens a popup on; a click anywhere else closes it. */
const CLICKABLE = ["crime-bubbles", "crime-dots", "schools-pins", "stations"];

/** Track width in pixels by zoom. */
const RAIL_WIDTHS: Record<number, number> = { 9: 1.8, 12: 3, 15: 4.8, 17: 6.75 };

const EMPTY: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

function feature(lon: number, lat: number, properties: GeoJSON.GeoJsonProperties) {
  return {
    type: "Feature" as const,
    geometry: { type: "Point" as const, coordinates: [lon, lat] },
    properties,
  };
}

export class Evidence {
  private readonly map: MapLibreMap;
  private readonly manifest: Manifest;
  private readonly data: Data;
  private readonly built = new Set<string>();
  /**
   * The build in flight per category. Two `show` calls for one category --
   * a reader tapping its card twice while its points are still arriving --
   * must not add its sources and layers twice, and awaiting the same promise
   * is how they do not.
   */
  private readonly building = new Map<string, Promise<void>>();
  // Not `closeOnClick`: the open popup's close handler still runs for the click
  // that opens the next one, and closes it the moment it appears.
  private readonly popup = new Popup({ closeButton: true, closeOnClick: false,
                                       maxWidth: "280px", offset: 12 });
  private streets: string[] | null = null;
  private categories: CrimeCategories | null = null;
  private current: string | null = null;
  private phase = "any";
  /** Which tap the open popup belongs to, so a late street lands on the right one. */
  private popupSeq = 0;

  constructor(map: MapLibreMap, manifest: Manifest, data: Data) {
    this.map = map;
    this.manifest = manifest;
    this.data = data;
    this.map.on("click", (event) => {
      const layers = CLICKABLE.filter((id) => this.map.getLayer(id));
      if (!layers.length ||
          !this.map.queryRenderedFeatures(event.point, { layers }).length) {
        this.popup.remove();
      }
    });
  }

  /** Show one category's evidence, or none of it. Idempotent. */
  async show(category: string | null, phase: string): Promise<void> {
    const phaseChanged = phase !== this.phase;
    // A popup belongs to the layer it came from: a school's Ofsted grade must
    // not be left sitting over the transport view.
    if (category !== this.current) this.popup.remove();
    this.phase = phase;
    this.current = category;
    const builders: Record<string, () => Promise<void>> = {
      crime: () => this.crime(),
      schools: () => this.schools(),
      transport: () => this.transport(),
    };
    const builder = category ? builders[category] : undefined;
    if (category && builder) {
      let pending = this.building.get(category);
      if (!pending) {
        pending = builder();
        this.building.set(category, pending);
      }
      await pending;
    }
    if (phaseChanged && this.built.has("schools")) this.applySchoolFilter();
    this.applyVisibility();
  }

  private applyVisibility(): void {
    const groups: Record<string, string[]> = {
      crime: ["crime-bubbles", "crime-bubble-count", "crime-dots", "crime-dot-count"],
      schools: ["schools-pins", "schools-letters"],
      transport: ["railways", "bus-stops", "stations", "stations-ring"],
    };
    for (const [category, ids] of Object.entries(groups)) {
      const visible = this.current === category;
      for (const id of ids) {
        if (!this.map.getLayer(id)) continue;
        this.map.setLayoutProperty(id, "visibility", visible ? "visible" : "none");
      }
    }
    if (this.current === null) this.popup.remove();
  }

  private applySchoolFilter(): void {
    // "Any" is every school; a phase keeps the schools that serve it, which for
    // an all-through school is both.
    const filter: unknown[] = this.phase === "any"
      ? ["all"]
      : ["==", ["get", this.phase === "primary" ? "p" : "s"], true];
    for (const id of ["schools-pins", "schools-letters"]) {
      if (this.map.getLayer(id)) this.map.setFilter(id, filter as never);
    }
  }

  // -- crime --------------------------------------------------------------

  private async crime(): Promise<void> {
    if (this.built.has("crime")) return;
    const points = await this.data.crime();
    const spec = this.manifest.points.crime;
    if (!points || !spec) return;
    this.built.add("crime");

    const features: GeoJSON.Feature[] = [];
    for (let i = 0; i < points.n.length; i++) {
      features.push(feature(points.lon[i]!, points.lat[i]!,
                            { n: points.n[i]!, s: points.street[i]!, i }));
    }
    this.map.addSource("crime", {
      type: "geojson",
      data: { type: "FeatureCollection", features },
      cluster: true,
      clusterRadius: CLUSTER_RADIUS,
      clusterMaxZoom: CLUSTER_MAX_ZOOM,
      // the bubble carries the offences it stands for, not the points
      clusterProperties: { n: ["+", ["get", "n"]] },
    });

    // MapLibre's expression types cannot see through a shared sub-expression,
    // so the assembled paint values are cast where they are used.
    const bubble: unknown[] = ["sqrt", ["max", ["get", "n"], 1]];
    this.map.addLayer({
      id: "crime-bubbles",
      type: "circle",
      source: "crime",
      filter: ["has", "point_count"],
      paint: {
        "circle-color": "#e34948",
        "circle-opacity": 0.82,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1.5,
        // Area grows with the count, capped well short of what the arithmetic
        // would give: London-wide, one bubble can stand for 160,000 offences,
        // and a circle scaled to that buries the raster it is evidence for.
        "circle-radius": ["interpolate", ["linear"], bubble,
                          3, 7, 20, 11, 60, 16, 200, 22] as never,
      },
    });
    this.map.addLayer({
      id: "crime-bubble-count",
      type: "symbol",
      source: "crime",
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["case",
          [">=", ["get", "n"], 1000],
          ["concat", ["to-string", ["/", ["round", ["/", ["get", "n"], 100]], 10]], "k"],
          ["to-string", ["get", "n"]]],
        "text-font": ["Noto Sans Regular"],
        "text-size": ["interpolate", ["linear"], bubble, 3, 9, 60, 11] as never,
        "text-allow-overlap": true,
      },
      paint: { "text-color": "#ffffff" },
    });
    this.map.addLayer({
      id: "crime-dots",
      type: "circle",
      source: "crime",
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-color": "#e34948",
        "circle-opacity": 0.85,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1,
        // A dot is a published point, so once the map is in a street it is a
        // marker to be read rather than a speck: the spread around that is the
        // count it holds.
        "circle-radius": ["interpolate", ["linear"], ["zoom"],
          13, ["interpolate", ["linear"], ["sqrt", ["max", ["get", "n"], 1]],
               1, 3.5, 5, 6, 15, 10, 40, 14],
          17, ["interpolate", ["linear"], ["sqrt", ["max", ["get", "n"], 1]],
               1, 9, 5, 13, 15, 20, 40, 28]] as never,
      },
    });
    this.map.addLayer({
      id: "crime-dot-count",
      type: "symbol",
      source: "crime",
      minzoom: DOT_COUNT_ZOOM,
      filter: ["!", ["has", "point_count"]],
      layout: {
        "text-field": ["to-string", ["get", "n"]],
        "text-font": ["Noto Sans Regular"],
        "text-size": 10,
        "text-allow-overlap": true,
      },
      paint: { "text-color": "#ffffff" },
    });

    this.map.on("click", "crime-bubbles", (event) => {
      void this.bubblePopup(event.features?.[0], event.lngLat);
    });
    this.map.on("click", "crime-dots", (event) => {
      void this.crimePopup(event.features?.[0], event.lngLat);
    });
    for (const id of ["crime-bubbles", "crime-dots"]) this.cursor(id);
  }

  /**
   * What a crime dot says, printed at once and completed when it can be.
   *
   * The count is in the dot already; the street it is "on or near" and the
   * offences by category live in tables that only a tap needs, so they are
   * fetched on the first tap and kept. Waiting for it before drawing anything would make the first
   * tap of a session feel broken, so the popup opens on what is known and the
   * street arrives into it -- unless the reader has tapped something else in
   * the meantime, which `seq` is what notices.
   */
  private async crimePopup(hit: MapGeoJSONFeature | undefined,
                           at: { lng: number; lat: number }): Promise<void> {
    if (!hit) return;
    const spec = this.manifest.points.crime;
    const n = Number(hit.properties?.["n"] ?? 0);
    const index = Number(hit.properties?.["s"] ?? -1);
    const point = Number(hit.properties?.["i"] ?? -1);
    const mine = ++this.popupSeq;

    const render = (): string => {
      const street = this.streets?.[index] ?? "";
      return `<div class="pop"><strong>${count(n)} ${n === 1 ? "offence" : "offences"}</strong>` +
        (street ? `<div class="pop-where">on or near ${escapeHtml(street)}</div>` : "") +
        categoryRows(this.categories, [point]) +
        `<div class="pop-note">${escapeHtml(spec?.period ?? "")}. Met Police, published to ` +
        `an anonymised point standing for at least eight addresses.</div></div>`;
    };

    this.popup.setLngLat(at).setHTML(render()).addTo(this.map);
    if (this.streets && this.categories) return;
    const [streets, categories] = await Promise.all([
      this.data.crimeStreets(), this.data.crimeCategories(),
    ]);
    this.streets = streets;
    this.categories = categories;
    if (mine === this.popupSeq && this.popup.isOpen()) this.popup.setHTML(render());
  }

  /**
   * What a bubble stands for: its offences summed by category over the points
   * inside it.
   */
  private async bubblePopup(hit: MapGeoJSONFeature | undefined,
                            at: { lng: number; lat: number }): Promise<void> {
    const id = hit?.properties?.["cluster_id"];
    const source = this.map.getSource("crime") as GeoJSONSource | undefined;
    if (id === undefined || !source) return;
    const spec = this.manifest.points.crime;
    const n = Number(hit?.properties?.["n"] ?? 0);
    const size = Number(hit?.properties?.["point_count"] ?? 0);
    const mine = ++this.popupSeq;
    let leaves: number[] | null = null;

    const render = (): string =>
      `<div class="pop"><strong>${count(n)} offences</strong>` +
      `<div class="pop-where">across ${count(size)} published points</div>` +
      (leaves ? categoryRows(this.categories, leaves)
              : `<div class="pop-note">Counting by category…</div>`) +
      `<div class="pop-note">${escapeHtml(spec?.period ?? "")}. Met Police recorded ` +
      `crime.</div></div>`;
    this.popup.setLngLat(at).setHTML(render()).addTo(this.map);
    const [features, categories] = await Promise.all([
      source.getClusterLeaves(Number(id), size, 0), this.data.crimeCategories(),
    ]);
    this.categories = categories;
    leaves = features.map((f) => Number(f.properties?.["i"] ?? -1));
    if (mine === this.popupSeq && this.popup.isOpen()) this.popup.setHTML(render());
  }

  // -- schools ------------------------------------------------------------

  private async schools(): Promise<void> {
    if (this.built.has("schools")) return;
    const schools = await this.data.schools();
    if (!schools) return;
    this.built.add("schools");

    const features: GeoJSON.Feature[] = [];
    for (let i = 0; i < schools.lon.length; i++) {
      const quality = schools.quality[i];
      const gradeIndex = schools.grade_i[i];
      features.push(feature(schools.lon[i]!, schools.lat[i]!, {
        name: schools.name[schools.name_i[i]!] ?? "",
        letter: schools.letter[i] ?? "·",
        q: quality ?? -1,
        grade: gradeIndex === undefined ? "" : schools.grades[gradeIndex] ?? "",
        inspected: schools.inspected[i] ?? 0,
        pupils: schools.pupils[i] ?? 0,
        p: schools.primary[i] === true,
        s: schools.secondary[i] === true,
      }));
    }
    this.map.addSource("schools", { type: "geojson",
                                    data: { type: "FeatureCollection", features } });

    // A grade this project turned into a number is turned back into a colour
    // here only; the words Ofsted used are what a tap prints.
    const fill: unknown[] = ["case", ["<", ["get", "q"], 0], UNGRADED,
      ["interpolate", ["linear"], ["get", "q"],
       0, GRADE_COLOURS[0]!, 0.35, GRADE_COLOURS[1]!, 0.6, GRADE_COLOURS[2]!,
       0.8, GRADE_COLOURS[3]!, 1, GRADE_COLOURS[4]!]];

    this.map.addLayer({
      id: "schools-pins",
      type: "circle",
      source: "schools",
      paint: {
        "circle-color": fill as never,
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 5, 12, 8, 15, 11, 17, 14],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1.5,
        "circle-opacity": 0.95,
      },
    });
    this.map.addLayer({
      id: "schools-letters",
      type: "symbol",
      source: "schools",
      minzoom: 11.5,
      layout: {
        "text-field": ["get", "letter"],
        "text-font": ["Noto Sans Regular"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 12, 9, 17, 12],
        "text-allow-overlap": true,
      },
      paint: { "text-color": "#ffffff" },
    });
    this.applySchoolFilter();

    this.map.on("click", "schools-pins", (event) => {
      const hit = event.features?.[0];
      if (!hit) return;
      const p = hit.properties ?? {};
      const grade = String(p["grade"] ?? "");
      const year = Number(p["inspected"] ?? 0);
      const pupils = Number(p["pupils"] ?? 0);
      const phase = p["p"] && p["s"] ? "all-through" : p["p"] ? "primary" : "secondary";
      const rows = [
        grade ? `<div class="pop-row"><span>Ofsted</span><b>${escapeHtml(grade)}</b></div>` : "",
        year ? `<div class="pop-row"><span>Last inspected</span><b>${year}</b></div>` : "",
        pupils ? `<div class="pop-row"><span>On roll</span><b>${count(pupils)}</b></div>` : "",
      ].join("");
      this.popup
        .setLngLat(event.lngLat)
        .setHTML(`<div class="pop"><strong>${escapeHtml(String(p["name"] ?? ""))}</strong>` +
                 `<div class="pop-where">${phase}</div>${rows}` +
                 (grade ? "" : `<div class="pop-note">Ofsted has named no grade for this ` +
                               `school; it is not ranked on one.</div>`) +
                 `</div>`)
        .addTo(this.map);
    });
    this.cursor("schools-pins");
  }

  // -- transport ----------------------------------------------------------

  private async transport(): Promise<void> {
    if (this.built.has("transport")) return;
    this.built.add("transport");
    const [railways, stations, bus] = await Promise.all([
      this.data.railways(), this.data.stations(), this.data.busStops(),
    ]);

    // The lines come from OpenStreetMap and are the one asset that may be
    // missing: without them the view is stations and stops, which is still the
    // transport layer, so the map is drawn either way.
    this.map.addSource("railways", { type: "geojson", data: railways?.geojson ?? EMPTY });
    // a tube line carries TfL's colour for it; other track is coloured by class
    const colour: unknown[] = ["coalesce", ["get", "colour"], ["match", ["get", "klass"],
      ...Object.entries(RAILWAY_COLOURS).flatMap(([klass, hex]) => [klass, hex]),
      RAILWAY_COLOURS["rail"]!]];
    // lines sharing track sit side by side, `o` line widths from its centre
    const offset = (width: number): unknown[] => ["*", ["coalesce", ["get", "o"], 0], width];
    this.map.addLayer({
      id: "railways",
      type: "line",
      source: "railways",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": colour as never,
        "line-opacity": ["case", ["has", "colour"], 1, 0.75],
        "line-width": ["interpolate", ["linear"], ["zoom"],
                       9, RAIL_WIDTHS[9]!, 12, RAIL_WIDTHS[12]!, 15, RAIL_WIDTHS[15]!,
                       17, RAIL_WIDTHS[17]!],
        "line-offset": ["interpolate", ["linear"], ["zoom"],
                        9, offset(RAIL_WIDTHS[9]!), 12, offset(RAIL_WIDTHS[12]!),
                        15, offset(RAIL_WIDTHS[15]!), 17, offset(RAIL_WIDTHS[17]!)] as never,
      },
    });

    if (bus) {
      const features: GeoJSON.Feature[] = [];
      for (let i = 0; i < bus.lon.length; i++) features.push(feature(bus.lon[i]!, bus.lat[i]!, {}));
      this.map.addSource("bus", { type: "geojson",
                                  data: { type: "FeatureCollection", features } });
      this.map.addLayer({
        id: "bus-stops",
        type: "circle",
        source: "bus",
        minzoom: BUS_ZOOM,
        paint: {
          "circle-color": "#2a78d6",
          "circle-opacity": 0.5,
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 15, 2.5, 17, 4],
        },
      });
    }

    if (stations) {
      const features: GeoJSON.Feature[] = [];
      for (let i = 0; i < stations.lon.length; i++) {
        features.push(feature(stations.lon[i]!, stations.lat[i]!, {
          name: stations.name[i] ?? "",
          lines: stations.lines[i] ?? "",
          mode: stations.mode[i] ?? "",
        }));
      }
      this.map.addSource("stations", { type: "geojson",
                                       data: { type: "FeatureCollection", features } });
      this.map.addLayer({
        id: "stations",
        type: "circle",
        source: "stations",
        paint: {
          "circle-color": "#ffffff",
          "circle-stroke-color": "#2a78d6",
          "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 9, 1.5, 15, 3],
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 3, 12, 5, 15, 7, 17, 9],
        },
      });
      this.map.on("click", "stations", (event) => {
        const hit = event.features?.[0];
        if (!hit) return;
        const p = hit.properties ?? {};
        const lines = showsLines(String(p["mode"] ?? "")) ? String(p["lines"] ?? "") : "";
        this.popup
          .setLngLat(event.lngLat)
          .setHTML(`<div class="pop"><strong>${escapeHtml(String(p["name"] ?? ""))}</strong>` +
                   `<div class="pop-where">${escapeHtml(String(p["mode"] ?? ""))}</div>` +
                   (lines ? `<div class="pop-row"><span>Lines</span><b>${escapeHtml(lines)}</b>` +
                            `</div>` : ""))
          .addTo(this.map);
      });
      this.cursor("stations");
    }
  }

  /** Whether a click landed on a dot, bubble, pin or station, which answers it with its own popup. */
  hitsMarker(point: { x: number; y: number }): boolean {
    const layers = CLICKABLE.filter((id) => this.map.getLayer(id));
    return this.current !== null && layers.length > 0 &&
      this.map.queryRenderedFeatures([point.x, point.y], { layers }).length > 0;
  }

  private cursor(id: string): void {
    this.map.on("mouseenter", id, () => {
      this.map.getCanvas().style.cursor = "pointer";
    });
    this.map.on("mouseleave", id, () => {
      this.map.getCanvas().style.cursor = "";
    });
  }
}

/** Offences by category summed over some crime points, largest first, as popup rows. */
function categoryRows(categories: CrimeCategories | null, points: number[]): string {
  if (!categories) return "";
  const k = categories.names.length;
  const sums = new Array<number>(k).fill(0);
  for (const point of points) {
    if (point < 0) continue;
    for (let j = 0; j < k; j++) sums[j]! += categories.counts[point * k + j] ?? 0;
  }
  const rows: [string, number][] = [];
  for (let j = 0; j < k; j++) {
    if (sums[j]! > 0) rows.push([categories.names[j]!, sums[j]!]);
  }
  rows.sort((a, b) => b[1] - a[1]);
  if (!rows.length) return "";
  return `<div class="pop-split">${rows.map(([name, v]) =>
    `<div class="pop-row"><span>${escapeHtml(name)}</span><b>${count(v)}</b></div>`).join("")}</div>`;
}

/** Rail and tram stations are shown by their mode alone, without the lines calling there. */
export function showsLines(mode: string): boolean {
  return mode !== "rail" && mode !== "tram";
}

/** What the transport view says when the lines could not be fetched. */
export function railwayNote(manifest: Manifest): string {
  return manifest.railways
    ? `${count(manifest.railways.count)} ways, OpenStreetMap`
    : "tube and rail lines unavailable in this build";
}
